/* eslint-disable lingui/text-restrictions */
/* eslint-disable lingui/no-unlocalized-strings */

import { t } from "@lingui/macro";
import {
  ArrowRight,
  Exam,
  Lightning,
  PenNib,
  SpeakerHigh,
  TextAa,
  TrendUp,
} from "@phosphor-icons/react";
import { Button } from "@reactive-resume/ui";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import { useAuthStore } from "@/client/stores/auth";

const aiActions = [
  {
    icon: <PenNib weight="duotone" className="h-5 w-5" />,
    label: "Improve",
    desc: "Rewrite with action verbs and measurable results",
    color: "text-blue-600 bg-blue-500/10",
  },
  {
    icon: <Exam weight="duotone" className="h-5 w-5" />,
    label: "Grammar",
    desc: "Fix spelling, grammar, and punctuation instantly",
    color: "text-emerald-600 bg-emerald-500/10",
  },
  {
    icon: <TextAa weight="duotone" className="h-5 w-5" />,
    label: "Expand",
    desc: "Turn brief notes into detailed bullet points",
    color: "text-violet-600 bg-violet-500/10",
  },
  {
    icon: <TrendUp weight="duotone" className="h-5 w-5" />,
    label: "Quantify",
    desc: "Add metrics and measurable outcomes to your achievements",
    color: "text-orange-600 bg-orange-500/10",
  },
  {
    icon: <SpeakerHigh weight="duotone" className="h-5 w-5" />,
    label: "Change Tone",
    desc: "Switch between Professional, Confident, Concise, and more",
    color: "text-pink-600 bg-pink-500/10",
  },
  {
    icon: <Lightning weight="duotone" className="h-5 w-5" />,
    label: "Generate",
    desc: "Create entire sections from just your job title",
    color: "text-amber-600 bg-amber-500/10",
  },
];

export const FeatureAIWriterSection = () => {
  const isLoggedIn = useAuthStore((s) => !!s.user);

  return (
    <section id="ai-writer" className="relative py-24 sm:py-32">
      <div className="container">
        <motion.div
          className="mx-auto max-w-3xl text-center"
          viewport={{ once: true }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          <p className="text-sm font-semibold uppercase tracking-wider text-primary">
            {t`AI-Powered Writing`}
          </p>
          <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
            {t`Write Your Resume with AI in Minutes`}
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-foreground/60">
            Stop staring at a blank page. CareerBldr's AI resume writer generates professional
            bullet points, summaries, and entire sections from your experience. Every line is
            original, quantified, and tailored to your career. Not pulled from a template library.
          </p>
        </motion.div>

        {/* Before → After real screenshots */}
        <motion.div
          className="mx-auto mt-14 grid max-w-5xl gap-6 sm:grid-cols-2"
          viewport={{ once: true }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0, transition: { delay: 0.1 } }}
        >
          <div className="relative">
            <span className="mb-3 inline-block rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700 dark:bg-red-500/20 dark:text-red-400">
              Before
            </span>
            <img
              src="/screenshots/ai-writer-before.png"
              alt="Resume bullet points before AI improvement — generic, no metrics"
              loading="lazy"
              decoding="async"
              className="w-full rounded-xl shadow-2xl ring-1 ring-foreground/10"
            />
          </div>
          <div className="relative">
            <span className="mb-3 inline-block rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700 dark:bg-green-500/20 dark:text-green-400">
              After — AI Improved
            </span>
            <img
              src="/screenshots/ai-writer-after.png"
              alt="Resume bullet points after AI improvement — quantified, action verbs, metrics"
              loading="lazy"
              decoding="async"
              className="w-full rounded-xl shadow-2xl ring-1 ring-foreground/10"
            />
          </div>
        </motion.div>

        {/* 6 AI action cards */}
        <div className="mx-auto mt-14 grid max-w-5xl gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {aiActions.map((action, i) => (
            <motion.div
              key={action.label}
              viewport={{ once: true }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0, transition: { delay: 0.05 * i } }}
              className="flex items-start gap-4 rounded-xl border border-border/50 bg-background p-5 transition-colors hover:border-border"
            >
              <div className={`shrink-0 rounded-lg p-2.5 ${action.color}`}>{action.icon}</div>
              <div>
                <h3 className="text-sm font-semibold">{action.label}</h3>
                <p className="mt-1 text-xs leading-relaxed text-foreground/50">{action.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="mt-12 text-center"
          viewport={{ once: true }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1, transition: { delay: 0.3 } }}
        >
          <Button asChild size="lg">
            <Link to={isLoggedIn ? "/dashboard" : "/auth/register"}>
              {isLoggedIn ? t`Go to Dashboard` : t`Try AI Writing Now`}
              <ArrowRight className="ml-2" weight="bold" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};
